import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { WEB_SERVICE_MIMS, pathologies } from "../../config/constants";
import { passwordValidation, isValidDate, isValidBirthdate, HeightValidation, isLessThanToday } from "../../config/functions";
import {
  isPhraseProjectHospital,
  isNestProjectHospital,
  isRGSICUProjectHospital,
  isAISNProjectHospital,
} from "../../config/projectFunctions";
// Components
import Loader from "../loader";
import Axios from "../../config/axios";
import { Form } from "./styles";
import PatientNavigation from "../patientNavigation";
import ActionsManagement from "./sections/actionsManagement";
import Title from "../title";
import PersonalInformation from "./sections/personalInformation";
import Pathology from "./sections/pathology";
import Training from "./sections/training";
import Apps from "./sections/apps";
import moment from "moment";

const EditPatient = () => {
  const [hospitalClinicians, setHospitalClinicians] = useState([]);
  const [user, setUser] = useState("");
  const [password, setPassword] = useState("");
  const [clinicianId, setClinicianId] = useState("");
  const [aisnGroup, setAisnGroup] = useState("");
  const [startingDate, setStartingDate] = useState("");
  const [gender, setGender] = useState("");
  const [skinColor, setSkinColor] = useState("");
  const [height, setHeight] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [civilStatus, setCivilStatus] = useState("");
  const [homeSupport, setHomeSupport] = useState("");
  const [education, setEducation] = useState("");
  const [work, setWork] = useState("");
  const [gameExperience, setGameExperience] = useState("");
  const [computerExperience, setComputerExperience] = useState("");
  const [otherInfo, setOtherInfo] = useState("");
  // Pathology
  const [patientVersion, setPatientVersion] = useState(0);
  const [pathology, setPathology] = useState("");
  const [strokeSide, setStrokeSide] = useState("");
  const [strokeType, setStrokeType] = useState("");
  const [strokeLocation, setStrokeLocation] = useState("");
  const [dateOfStroke, setDateOfStroke] = useState("");
  const [currentStartingDate, setCurrentStartingDate] = useState("");
  const [hasHeminegligence, setHasHeminegligence] = useState(0);
  const [pareticSide, setPareticSide] = useState("LEFT");
  const [comorbidity, setComorbidity] = useState("");
  // Training
  const [upperExtremityToTrain, setUpperExtremityToTrain] = useState("BOTH");
  const [handRaisingCapacity, setHandRaisingCapacity] = useState("NONE");
  const [cognitiveFunctionLevel, setCognitiveFunctionLevel] = useState("LOW");

  const [appsArray, setAppsArray] = useState([]);
  const [personalInfo, setPersonalInfo] = useState({});
  const [pathologyInfo, setPathologyInfo] = useState({});
  const [appsInfo, setAppsInfo] = useState([]);
  // handle errors
  const [passwordError, setPasswordError] = useState(false);
  const [clinicianIdError, setClinicianIdError] = useState(false);
  const [startingDateError, setStartingDateError] = useState(false);
  const [genderError, setGenderError] = useState(false);
  const [heightError, setHeightError] = useState(false);
  const [dateOfBirthError, setDateOfBirthError] = useState(false);
  const [civilStatusError, setCivilStatusError] = useState(false);
  const [homeSupportError, setHomeSupportError] = useState(false);
  const [educationError, setEducationError] = useState(false);
  const [workError, setWorkError] = useState(false);
  const [pathologyError, setPathologyError] = useState(false);
  const [strokeSideError, setStrokeSideError] = useState(false);
  const [strokeTypeError, setStrokeTypeError] = useState(false);
  const [strokeLocationError, setStrokeLocationError] = useState(false);
  const [dateOfStrokeError, setDateOfStrokeError] = useState(false);
  const [comorbidityError, setComorbidityError] = useState(false);

  const [hospitalIsPartOfAProject, setHospitalIsPartOfAProject] = useState(false);
  const [isAISNHospital, setIsAISNHospital] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSubmittingData, setIsSubmittingData] = useState(false);
  const [disabledInputs, setDisabledInputs] = useState(true);
  const [isRechargeComponent, setIsRechargeComponent] = useState(false);

  const { patientId } = useParams();
  const [t] = useTranslation("global");
  const clinicianData = useSelector(({ clinician }) => clinician.data);

  // Fetching hospital clinicians
  useEffect(() => {
    if (clinicianData?.HOSPITAL_ID) {
      Axios("get", `${WEB_SERVICE_MIMS}/hospitals/get-clinicians/${clinicianData.HOSPITAL_ID}`)
        .then(({ data }) => {
          setHospitalClinicians(data);
        })
        .catch((error) => {
          console.log("Get hospital clinicians: ", error);
        });
    }

    return () => {
      setHospitalClinicians([]);
    };
  }, [clinicianData.HOSPITAL_ID]);

  // Fetch patient data
  useEffect(() => {
    if (clinicianData?.token) {
      Axios("get", `${WEB_SERVICE_MIMS}/patients/fetch/${patientId}`)
        .then(({ data }) => {
          if (data.patientClinicianId) setClinicianId(data.patientClinicianId);
          setUser(data.PATIENT_USER);
          setGender(data.GENDER);
          setSkinColor(data.SKIN_COLOR);
          setHeight(data.PTN_HEIGHT_CM);
          setDateOfBirth(data.BIRTH_DATE);
          data.VIDEOGAME_EXP === null ? setGameExperience("") : setGameExperience(data.VIDEOGAME_EXP);
          data.COMPUTER_EXP === null ? setComputerExperience("") : setComputerExperience(data.COMPUTER_EXP);
          data.COMMENTS === null ? setOtherInfo("") : setOtherInfo(data.COMMENTS);
          setPatientVersion(Number(data.VERSION) + 1);
          setPathology(Number(data.PATHOLOGY));
          setHasHeminegligence(data.HAS_HEMINEGLIGENCE);
          setUpperExtremityToTrain(data.UPPER_EXTREMITY_TO_TRAIN);
          setHandRaisingCapacity(data.HAND_RAISING_CAPACITY);
          setCognitiveFunctionLevel(data.COGNITIVE_FUNCTION_LEVEL);
          data.PARETIC_SIDE !== "NONE" && setPareticSide(data.PARETIC_SIDE);
          if (Number(data.PATHOLOGY) === pathologies.strokeId) setDateOfStroke(data.STROKE_DATE);
          setAppsArray(data.SOFTWARE_ID);
          if (isAISNHospital) {
            setAisnGroup(data.aisnData.AISN_GROUP);
            setStartingDate(new Date(moment(data.aisnData.ROUTINE_STARTING_DATE, "DD/MM/YYYY")));
            setCurrentStartingDate(data.aisnData.ROUTINE_STARTING_DATE);
            setCivilStatus(data.aisnData.CIVIL_STATUS);
            setHomeSupport(data.aisnData.HOME_SUPPORT);
            setEducation(data.aisnData.EDUCATION);
            setWork(data.aisnData.WORK);
            setStrokeSide(data.aisnData.STROKE_SIDE);
            setStrokeType(data.aisnData.STROKE_TYPE);
            setStrokeLocation(data.aisnData.STROKE_LOCATION);
            setComorbidity(data.aisnData.COMORBIDITY);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.log("url error: ", error);
        });
    }
  }, [clinicianData, patientId, isAISNHospital]);

  // Set projects flag
  useEffect(() => {
    const handleSetAISNProject = async () => {
      const isPHRASEHospital = await isPhraseProjectHospital(clinicianData.HOSPITAL_NAME);
      const isNESTHospital = await isNestProjectHospital(clinicianData.HOSPITAL_NAME);
      const isRGSICUHospital = await isRGSICUProjectHospital(clinicianData.HOSPITAL_NAME);

      setHospitalIsPartOfAProject(isPHRASEHospital || isNESTHospital || isRGSICUHospital);
      const isAISNHospital = await isAISNProjectHospital(clinicianData.HOSPITAL_NAME);
      setIsAISNHospital(isAISNHospital);
    };
    handleSetAISNProject();

    return () => {
      setIsAISNHospital(false);
    };
  }, [clinicianData]);

  const renderPatientNavigation = () => {
    return <PatientNavigation patientId={patientId} section="profile" rechargeComponent={isRechargeComponent} />;
  };

  const renderActionsManagement = () => {
    return (
      <ActionsManagement
        handleSubmit={handleSubmit}
        isSubmittingData={isSubmittingData}
        disabledInputs={disabledInputs}
        setDisabledInputs={setDisabledInputs}
        patientId={patientId}
        patientVersion={patientVersion}
      />
    );
  };

  const renderFormSections = () => {
    return (
      <>
        <PersonalInformation
          disabledInputs={disabledInputs}
          hospitalClinicians={hospitalClinicians}
          user={user}
          password={password}
          setPassword={setPassword}
          passwordError={passwordError}
          setPasswordError={setPasswordError}
          clinicianId={clinicianId}
          clinicianIdError={clinicianIdError}
          setClinicianIdError={setClinicianIdError}
          setClinicianId={setClinicianId}
          aisnGroup={aisnGroup}
          currentStartingDate={currentStartingDate}
          startingDate={startingDate}
          setStartingDate={setStartingDate}
          startingDateError={startingDateError}
          setStartingDateError={setStartingDateError}
          gender={gender}
          setGender={setGender}
          genderError={genderError}
          setGenderError={setGenderError}
          height={height}
          setHeight={setHeight}
          heightError={heightError}
          setHeightError={setHeightError}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          dateOfBirthError={dateOfBirthError}
          setDateOfBirthError={setDateOfBirthError}
          civilStatus={civilStatus}
          setCivilStatus={setCivilStatus}
          civilStatusError={civilStatusError}
          setCivilStatusError={setCivilStatusError}
          homeSupport={homeSupport}
          setHomeSupport={setHomeSupport}
          homeSupportError={homeSupportError}
          setHomeSupportError={setHomeSupportError}
          education={education}
          setEducation={setEducation}
          educationError={educationError}
          setEducationError={setEducationError}
          work={work}
          setWork={setWork}
          workError={workError}
          setWorkError={setWorkError}
          gameExperience={gameExperience}
          setGameExperience={setGameExperience}
          computerExperience={computerExperience}
          setComputerExperience={setComputerExperience}
          otherInfo={otherInfo}
          setOtherInfo={setOtherInfo}
          skinColor={skinColor}
          setSkinColor={setSkinColor}
          pareticSide={pareticSide}
          patientVersion={patientVersion}
          setPersonalInfo={setPersonalInfo}
        />
        <Pathology
          disabledInputs={disabledInputs}
          pathology={pathology}
          setPathology={setPathology}
          pathologyError={pathologyError}
          setPathologyError={setPathologyError}
          strokeSide={strokeSide}
          setStrokeSide={setStrokeSide}
          strokeSideError={strokeSideError}
          setStrokeSideError={setStrokeSideError}
          strokeType={strokeType}
          setStrokeType={setStrokeType}
          strokeTypeError={strokeTypeError}
          setStrokeTypeError={setStrokeTypeError}
          strokeLocation={strokeLocation}
          setStrokeLocation={setStrokeLocation}
          strokeLocationError={strokeLocationError}
          setStrokeLocationError={setStrokeLocationError}
          dateOfStroke={dateOfStroke}
          setDateOfStroke={setDateOfStroke}
          dateOfStrokeError={dateOfStrokeError}
          setDateOfStrokeError={setDateOfStrokeError}
          hasHeminegligence={hasHeminegligence}
          setHasHeminegligence={setHasHeminegligence}
          pareticSide={pareticSide}
          setPareticSide={setPareticSide}
          comorbidity={comorbidity}
          setComorbidity={setComorbidity}
          comorbidityError={comorbidityError}
          setComorbidityError={setComorbidityError}
          personalInfo={personalInfo}
          setPersonalInfo={setPersonalInfo}
          setPathologyInfo={setPathologyInfo}
          patientVersion={patientVersion}
        />
        <Training
          disabledInputs={disabledInputs}
          patientVersion={patientVersion}
          upperExtremityToTrain={upperExtremityToTrain}
          setUpperExtremityToTrain={setUpperExtremityToTrain}
          personalInfo={personalInfo}
          setPersonalInfo={setPersonalInfo}
          handRaisingCapacity={handRaisingCapacity}
          setHandRaisingCapacity={setHandRaisingCapacity}
          cognitiveFunctionLevel={cognitiveFunctionLevel}
          setCognitiveFunctionLevel={setCognitiveFunctionLevel}
        />
        {!isAISNHospital && hospitalIsPartOfAProject && (
          <>
            <Title title={t("rgsPrescriptions_title")} marginBottom="10px" width910="100%" />
            <Apps appsArray={appsArray} disabledInputs={disabledInputs} appsInfo={appsInfo} setAppsInfo={setAppsInfo} />
          </>
        )}
      </>
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmittingData(true);
    if (
      clinicianId === "" ||
      gender === "" ||
      height === "" ||
      pathology === "" ||
      dateOfBirth === "" ||
      (isAISNHospital &&
        (startingDate === "" ||
          civilStatus === "" ||
          homeSupport === "" ||
          education === "" ||
          work === "" ||
          strokeSide === "" ||
          strokeType === "" ||
          strokeLocation === "" ||
          comorbidity === "")) ||
      (Number(pathology) === pathologies.strokeId && dateOfStroke === "")
    ) {
      if (password !== "" && !passwordValidation(password)) setPasswordError(true);
      if (clinicianId === "") setClinicianIdError(true);
      if (startingDate === "") setStartingDateError(true);
      if (gender === "") setGenderError(true);
      if (height === "") setHeightError(true);
      if (dateOfBirth === "") setDateOfBirthError(true);
      if (civilStatus === "") setCivilStatusError(true);
      if (homeSupport === "") setHomeSupportError(true);
      if (education === "") setEducationError(true);
      if (work === "") setWorkError(true);
      if (pathology === "") setPathologyError(true);
      if (strokeSide === "") setStrokeSideError(true);
      if (strokeType === "") setStrokeTypeError(true);
      if (strokeLocation === "") setStrokeLocationError(true);
      if (comorbidity === "") setComorbidityError(true);
      if (Number(pathology) === pathologies.strokeId && dateOfStroke === "") setDateOfStrokeError(true);
      setIsSubmittingData(false);
      return toast.error(t("emptyDataMessage"));
    }
    if (password !== "" && !passwordValidation(password)) {
      setPasswordError(true);
      return toast.error(t("profileMessage_passwordMessage"));
    }
    if (!HeightValidation(height)) {
      setHeightError(true);
      setIsSubmittingData(false);
      return toast.error(t("profileMessage_heightMessage"));
    }
    if (!isValidBirthdate(dateOfBirth)) {
      setDateOfBirthError(true);
      setIsSubmittingData(false);
      return toast.error(t("profileMessage_birthDateMessage"));
    }
    if (Number(pathology) === pathologies.strokeId && !isValidDate(dateOfStroke)) {
      setDateOfStrokeError(true);
      setIsSubmittingData(false);
      return toast.error(t("profileMessage_strokeDateMessage"));
    }

    const parsedStartingDate = moment(startingDate).format("DD/MM/YYYY");

    if (
      isAISNHospital &&
      (!moment(parsedStartingDate, "DD/MM/YYYY", true).isValid() ||
        (isLessThanToday(parsedStartingDate) && moment(startingDate).format("DD/MM/YYYY") !== currentStartingDate))
    ) {
      toast.error(t("Enter a valid starting date"));
      setStartingDateError(true);
      setIsSubmittingData(false);
      return;
    }

    if (appsArray.length === 0) {
      setIsSubmittingData(false);
      return toast.error(t("profileMessage_applicationsMessage"));
    }

    let data = {
      appsInfo,
      pathologyInfo,
      personalInfo,
      clinicianId,
      isAISNHospital: isAISNHospital,
    };
    if (Number(pathology) !== pathologies.strokeId) {
      personalInfo.PARETIC_SIDE = "NONE";
    }
    if (data.personalInfo.VERSION) {
      setPatientVersion(patientVersion + 1);
    }

    if (isAISNHospital) {
      data.aisnInfo = {
        CIVIL_STATUS: civilStatus,
        HOME_SUPPORT: homeSupport,
        EDUCATION: education,
        WORK: work,
        STROKE_SIDE: strokeSide,
        STROKE_TYPE: strokeType,
        STROKE_LOCATION: strokeLocation,
        ROUTINE_STARTING_DATE: moment(startingDate).format("YYYY/MM/DD"),
        COMORBIDITY: comorbidity,
      };
    }

    Axios("put", `${WEB_SERVICE_MIMS}/patients/update/${patientId}`, data)
      .then(({ data }) => {
        if (data.updatedPatient) {
          setDisabledInputs(true);
          setIsSubmittingData(false);
          setIsRechargeComponent(!isRechargeComponent);
          toast.success(t("editPatient_patientUpdatedMessage"));
        } else {
          setIsSubmittingData(false);
          toast.error(t("sendDataErrorMessage"));
        }
      })
      .catch((error) => {
        setIsSubmittingData(false);
        toast.error(t("sendDataErrorMessage"));
        console.log("url error: ", error);
      });
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          {renderPatientNavigation()}
          {renderActionsManagement()}
          <Title title={t("editPatient_patientDataTitle")} marginBottom="10px" />
          <Form onSubmit={handleSubmit}>{renderFormSections()}</Form>
        </>
      )}
    </>
  );
};

export default EditPatient;
